import { call, fork, take, put } from 'redux-saga/effects'
import { bindAsyncAction } from 'typescript-fsa-redux-saga'
import { Action } from 'typescript-fsa'

import {
  DefaultApi,
  EditorStatisticsApi,
  IntervalStatisticsApi, PostTemplateApi
} from '../../api/instances'
import { Severity, EventType } from '@professocial/professocialTypes/types/messageTypes'
import { requestFailed, requestFinished, requestStarted, setMessage } from '../common/common.actions'

import {
  getEditorInstagramPageStatistics,
  getEditorFacebookPageStatistics,
  getEditorFacebookPageStatisticsInitiated,
  getEditorInstagramPageStatisticsInitiated,
  exportFacebookPageStatisticsForRange,
  exportFacebookPageStatisticsForRangeInitiated,
  exportInstagramPageStatisticsForRange,
  exportInstagramPageStatisticsForRangeInitiated,
  getInsightsForPublishedPosts,
  getInsightsForPublishedPostsInitiated,
  getPublishedTemplateCountInitiated,
  getPublishedTemplateCount,
  getPublishedPostsByTemplateCount,
  getPublishedPostsByTemplateCountInitiated,
  getInsightsForPublishedPostsBefore,
  getInsightsForPublishedPostsBeforeInitiated,
  exportPublishedPostsCountForTemplateStatistics,
  exportPublishedPostsCountForTemplateStatisticsInitiated,
  exportPublishedTemplatesByCreatorForRange,
  exportPublishedTemplatesByCreatorForRangeInitiated,
  getInsightsForPublishedTemplatesInRange,
  getInsightsForPublishedTemplatesInRangeInitiated, findPostTemplatesByMetric, findPostTemplatesByMetricInitiated,
} from './statistics.editor.actions'
import { GetFacebookPageStatisticsRequest, GetInstagramPageStatisticsRequest } from '@professocialApi/professocial-core-frontend-api'
import {
  ExportFacebookPageStatisticsForRangeRequest,
  ExportInstagramPageStatisticsForRangeRequest,
  ExportPublishedPostsCountForTemplateStatisticsRequest, ExportPublishedTemplatesByCreatorForRangeRequest
} from '@professocialApi/professocial-core-frontend-api/apis/DefaultApi'
import i18n from 'i18next'
import {
  GetAggregatedInsightsForPublishedPostsInRangeRequest, GetInsightsForPublishedTemplatesInRangeRequest,
  GetPublishedPostsByTemplateCountRequest,
  GetPublishedTemplateCountRequest
} from '@3m5/sm_publish-frontend-api/apis/IntervalStatisticsApi'
import { FindPostTemplatesByMetricRequest } from '@3m5/sm_publish-frontend-api/apis/PostTemplateApi'

// getFacebookPageStatistics
const getFacebookPageStatisticsWorker = bindAsyncAction(getEditorFacebookPageStatistics)(
  function * (params: GetFacebookPageStatisticsRequest): any {
    return yield call([EditorStatisticsApi, EditorStatisticsApi.getFacebookPageStatistics], params)
  },
)

function * watchGetFacebookPageStatisticsInitiated () {
  while (true) {
    const action: Action<GetFacebookPageStatisticsRequest> = yield take(getEditorFacebookPageStatisticsInitiated)
    yield put(requestStarted({ type: action.type }))
    try {
      yield getFacebookPageStatisticsWorker(action.payload)
      yield put(requestFinished({ type: action.type }))
    } catch (e) {
      yield put(requestFailed({
        type: action.type,
        error: e,
      }))
      yield put(setMessage({
        severity: Severity.error,
        message: 'messages:getFacebookPageStatisticsError',
        eventType: EventType.update,
      }))
      console.error('error in watchGetFacebookPageStatisticsInitiated: ', e)
    }
  }
}

// getInstagramPageStatistics
const getInstagramPageStatisticsWorker = bindAsyncAction(getEditorInstagramPageStatistics)(
  function * (params: GetInstagramPageStatisticsRequest): any {
    return yield call([EditorStatisticsApi, EditorStatisticsApi.getInstagramPageStatistics], params)
  },
)

function * watchGetInstagramPageStatisticsInitiated () {
  while (true) {
    const action: Action<GetInstagramPageStatisticsRequest> = yield take(getEditorInstagramPageStatisticsInitiated)
    yield put(requestStarted({ type: action.type }))
    try {
      yield getInstagramPageStatisticsWorker(action.payload)
      yield put(requestFinished({ type: action.type }))
    } catch (e) {
      yield put(requestFailed({
        type: action.type,
        error: e,
      }))
      yield put(setMessage({
        severity: Severity.error,
        message: 'messages:getInstagramPageStatisticsError',
        eventType: EventType.update,
      }))
      console.error('error in watchGetInstagramPageStatisticsInitiated: ', e)
    }
  }
}

// findPostTemplatesByMetric
const findPostTemplatesByMetricWorker = bindAsyncAction(findPostTemplatesByMetric)(
  function * (params: FindPostTemplatesByMetricRequest): any {
    return yield call([PostTemplateApi, PostTemplateApi.findPostTemplatesByMetric], params)
  },
)

function * watchFindPostTemplatesByMetricInitiated () {
  while (true) {
    const action: Action<FindPostTemplatesByMetricRequest> = yield take(findPostTemplatesByMetricInitiated)
    yield put(requestStarted({ type: action.type }))
    try {
      yield findPostTemplatesByMetricWorker(action.payload)
      yield put(requestFinished({ type: action.type }))
    } catch (e) {
      yield put(requestFailed({
        type: action.type,
        error: e,
      }))
      yield put(setMessage({
        severity: Severity.error,
        message: i18n.t('coreMessages:loadingError',
          { context: i18n.t('coreDashboard:templates') }),
        eventType: EventType.load,
      }))
      console.error('error in watchFindPostTemplatesByMetricInitiated: ', e)
    }
  }
}

// exportFacebookPageStatisticsForRange
const exportFacebookPageStatisticsForRangeWorker = bindAsyncAction(exportFacebookPageStatisticsForRange)(
  function * (params: ExportFacebookPageStatisticsForRangeRequest): any {
    return yield call([DefaultApi, DefaultApi.exportFacebookPageStatisticsForRange], params)
  },
)

function * watchExportFacebookPageStatisticsForRangeInitiated () {
  while (true) {
    const action: Action<ExportFacebookPageStatisticsForRangeRequest> = yield take(exportFacebookPageStatisticsForRangeInitiated)
    try {
      yield exportFacebookPageStatisticsForRangeWorker(action.payload)
    } catch (e) {
      yield put(setMessage({
        severity: Severity.error,
        message: 'messages:exportFacebookPageStatisticsForRangeError',
        eventType: EventType.update,
      }))
      console.error('error in watchExportFacebookPageStatisticsForRangeInitiated: ', e)
    }
  }
}

// exportFacebookPageStatisticsForRange
const exportInstagramPageStatisticsForRangeWorker = bindAsyncAction(exportInstagramPageStatisticsForRange)(
  function * (params: ExportInstagramPageStatisticsForRangeRequest): any {
    return yield call([DefaultApi, DefaultApi.exportInstagramPageStatisticsForRange], params)
  },
)

function * watchExportInstagramPageStatisticsForRangeInitiated () {
  while (true) {
    const action: Action<ExportInstagramPageStatisticsForRangeRequest> = yield take(exportInstagramPageStatisticsForRangeInitiated)
    try {
      yield exportInstagramPageStatisticsForRangeWorker(action.payload)
    } catch (e) {
      yield put(setMessage({
        severity: Severity.error,
        message: 'messages:exportInstagramPageStatisticsForRangeError',
        eventType: EventType.update,
      }))
      console.error('error in watchExportInstagramPageStatisticsForRangeInitiated: ', e)
    }
  }
}


// getInsightsForPublishedPosts
const getInsightsForPublishedPostsWorker = bindAsyncAction(getInsightsForPublishedPosts)(
  function * (params: GetAggregatedInsightsForPublishedPostsInRangeRequest): any {
    return yield call([IntervalStatisticsApi, IntervalStatisticsApi.getAggregatedInsightsForPublishedPostsInRange], params)
  },
)

function * watchGetInsightsForPublishedPostsWorkerInitiated () {
  while (true) {
    const action: Action<GetAggregatedInsightsForPublishedPostsInRangeRequest> = yield take(getInsightsForPublishedPostsInitiated)
    try {
      yield getInsightsForPublishedPostsWorker(action.payload)
    } catch (e) {
      console.error('error in watchGetInsightsForPublishedPostsInitiated: ', e)
    }
  }
}

// getInsightsForPublishedPostsBefore
const getInsightsForPublishedPostsBeforeWorker = bindAsyncAction(getInsightsForPublishedPostsBefore)(
  function * (params: GetAggregatedInsightsForPublishedPostsInRangeRequest): any {
    return yield call([IntervalStatisticsApi, IntervalStatisticsApi.getAggregatedInsightsForPublishedPostsInRange], params)
  },
)

function * watchGetInsightsForPublishedPostsBeforeWorkerInitiated () {
  while (true) {
    const action: Action<GetAggregatedInsightsForPublishedPostsInRangeRequest> = yield take(getInsightsForPublishedPostsBeforeInitiated)
    try {
      yield getInsightsForPublishedPostsBeforeWorker(action.payload)
    } catch (e) {
      console.error('error in watchGetInsightsForPublishedPostsBeforeInitiated: ', e)
    }
  }
}

// getPublishedTemplateCount
const getPublishedTemplateCountWorker = bindAsyncAction(getPublishedTemplateCount)(
  function * (params: GetPublishedTemplateCountRequest): any {
    return yield call([IntervalStatisticsApi, IntervalStatisticsApi.getPublishedTemplateCount], params)
  },
)

function * watchGetPublishedTemplateCountWorkerInitiated () {
  while (true) {
    const action: Action<GetPublishedTemplateCountRequest> = yield take(getPublishedTemplateCountInitiated)
    try {
      yield getPublishedTemplateCountWorker(action.payload)
    } catch (e) {
      console.error('error in watchGetPublishedTemplateCountInitiated: ', e)
    }
  }
}

// getPublishedPostsByTemplateCount
const getPublishedPostsByTemplateCountWorker = bindAsyncAction(getPublishedPostsByTemplateCount)(
  function * (params: GetPublishedPostsByTemplateCountRequest): any {
    return yield call([IntervalStatisticsApi, IntervalStatisticsApi.getPublishedPostsByTemplateCount], params)
  },
)

function * watchGetPublishedPostsByTemplateCountInitiated () {
  while (true) {
    const action: Action<GetPublishedPostsByTemplateCountRequest> = yield take(getPublishedPostsByTemplateCountInitiated)
    try {
      yield getPublishedPostsByTemplateCountWorker(action.payload)
    } catch (e) {
      console.error('error in watchGetPublishedPostsByTemplateCountInitiated: ', e)
    }
  }
}

// exportPublishedPostsCountForTemplateStatistics
const exportPublishedPostsCountForTemplateStatisticsWorker = bindAsyncAction(exportPublishedPostsCountForTemplateStatistics)(
  function * (params: ExportPublishedPostsCountForTemplateStatisticsRequest): any {
    return yield call([DefaultApi, DefaultApi.exportPublishedPostsCountForTemplateStatistics], params)
  },
)

function * watchExportPublishedPostsCountForTemplateStatisticsInitiated () {
  while (true) {
    const action: Action<ExportPublishedPostsCountForTemplateStatisticsRequest> = yield take(exportPublishedPostsCountForTemplateStatisticsInitiated)
    try {
      yield exportPublishedPostsCountForTemplateStatisticsWorker(action.payload)
    } catch (e) {
      console.error('error in watchExportPublishedPostsCountForTemplateStatisticsInitiated: ', e)
    }
  }
}

// exportPublishedTemplatesByCreatorForRange
const exportPublishedTemplatesByCreatorForRangeWorker = bindAsyncAction(exportPublishedTemplatesByCreatorForRange)(
  function * (params: ExportPublishedTemplatesByCreatorForRangeRequest): any {
    return yield call([DefaultApi, DefaultApi.exportPublishedTemplatesByCreatorForRange], params)
  },
)

function * watchExportPublishedTemplatesByCreatorForRangeInitiated () {
  while (true) {
    const action: Action<ExportPublishedTemplatesByCreatorForRangeRequest> = yield take(exportPublishedTemplatesByCreatorForRangeInitiated)
    try {
      yield exportPublishedTemplatesByCreatorForRangeWorker(action.payload)
    } catch (e) {
      console.error('error in watchExportPublishedTemplatesByCreatorForRangeInitiated: ', e)
    }
  }
}

// getInsightsForPublishedTemplatesInRange
const getInsightsForPublishedTemplatesInRangeWorker = bindAsyncAction(getInsightsForPublishedTemplatesInRange)(
  function * (params: GetInsightsForPublishedTemplatesInRangeRequest): any {
    return yield call([IntervalStatisticsApi, IntervalStatisticsApi.getInsightsForPublishedTemplatesInRange], params)
  },
)

function * watchGetInsightsForPublishedTemplatesInRangeInitiated () {
  while (true) {
    const action: Action<GetInsightsForPublishedTemplatesInRangeRequest> = yield take(getInsightsForPublishedTemplatesInRangeInitiated)
    try {
      yield getInsightsForPublishedTemplatesInRangeWorker(action.payload)
    } catch (e) {
      console.error('error in watchGetInsightsForPublishedTemplatesInRangeInitiated: ', e)
    }
  }
}


export function * statisticsEditorSagas () {
  yield fork(watchGetFacebookPageStatisticsInitiated)
  yield fork(watchGetInstagramPageStatisticsInitiated)
  yield fork(watchFindPostTemplatesByMetricInitiated)
  yield fork(watchExportFacebookPageStatisticsForRangeInitiated)
  yield fork(watchExportInstagramPageStatisticsForRangeInitiated)
  yield fork(watchGetInsightsForPublishedPostsWorkerInitiated)
  yield fork(watchGetInsightsForPublishedPostsBeforeWorkerInitiated)
  yield fork(watchGetPublishedTemplateCountWorkerInitiated)
  yield fork(watchGetPublishedPostsByTemplateCountInitiated)
  yield fork(watchExportPublishedPostsCountForTemplateStatisticsInitiated)
  yield fork(watchExportPublishedTemplatesByCreatorForRangeInitiated)
  yield fork(watchGetInsightsForPublishedTemplatesInRangeInitiated)
}
